(() => {
  'use strict';

  if (window.CalmCRO) return;

  const script = document.currentScript;
  const environments = ['live', 'preview', 'editor'];
  const pageTypes = ['home', 'product', 'quiz', 'cart', 'collection', 'other'];
  const environment = environments.includes(script?.dataset.environment) ? script.dataset.environment : 'preview';
  const pageType = pageTypes.includes(script?.dataset.pageType) ? script.dataset.pageType : 'other';
  const context = Object.freeze({ release: 'tracking_v3', environment, page_type: pageType });
  const sizes = ['40x50', '50x70', '70x100'];
  const handles = ['museum-quality-matte-paper-poster', 'calmness', 'journey', 'resilience', 'stillness'];
  const variantSizes = Object.freeze({
    '55247741747574': '40x50', '55247741780342': '50x70', '55461725176182': '70x100',
    '55248000123254': '40x50', '55248000156022': '50x70', '55461729993078': '70x100',
    '55247408431478': '40x50', '55247408464246': '50x70', '55461720031606': '70x100',
    '55248019554678': '40x50', '55248019587446': '50x70', '55461720981878': '70x100',
    '55248038101366': '40x50', '55248038134134': '50x70', '55461716296054': '70x100',
  });
  const schemas = Object.freeze({
    quiz_started: {},
    quiz_step_completed: { step: [1, 2, 3, 4, 5, 6] },
    quiz_result_viewed: {},
    quiz_add_success: {},
    size_selected: { source: ['product', 'guide', 'quiz', 'pair'], size: sizes },
    size_guide_used: { kind: ['jump', 'comparison'] },
    pair_submitted: { size: sizes },
    pair_add_success: { size: sizes },
    product_selected: { source: ['collection', 'pair', 'recommendation'], handle: handles },
    cart_error: { source: ['product', 'quiz', 'pair', 'cart'], stage: ['api', 'transport', 'render'], committed: [true, false] },
    checkout_clicked: { source: ['drawer', 'cart'] },
  });
  const onceKeys = ['started', 'step_1', 'step_2', 'step_3', 'step_4', 'step_5', 'step_6', 'result', 'error'];
  const seen = new WeakMap();
  const guideIntents = new WeakMap();
  const confirmedIntents = new WeakSet();
  let taggedClarity;

  function allowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  }

  function payloadFor(name, data) {
    if (!Object.hasOwn(schemas, name) || !data || typeof data !== 'object' || Array.isArray(data)) return null;
    const schema = schemas[name];
    const keys = Reflect.ownKeys(data);
    if (keys.length !== Object.keys(schema).length) return null;
    const clean = {};
    for (const key of keys) {
      if (typeof key !== 'string' || !Object.hasOwn(schema, key) || !schema[key].includes(data[key])) return null;
      clean[key] = data[key];
    }
    return clean;
  }

  function ignoreRejection(result) {
    if (result && typeof result.catch === 'function') result.catch(() => {});
  }

  function syncContext() {
    try {
      const provider = window.clarity;
      if (typeof provider !== 'function' || !allowed()) return false;
      if (taggedClarity !== provider) {
        for (const [key, value] of Object.entries(context)) {
          if (!allowed()) return false;
          ignoreRejection(provider('set', 'calm_' + key, value));
        }
        taggedClarity = provider;
      }
      return true;
    } catch {
      return false;
    }
  }

  function sendClarity(name, data) {
    try {
      if (!syncContext() || !allowed()) return;
      const eventName = name === 'quiz_step_completed' ? 'quiz_step_' + data.step + '_completed' : name;
      ignoreRejection(window.clarity('event', 'calm_' + eventName));
    } catch {}
  }

  function emit(name, data = {}) {
    try {
      const clean = payloadFor(name, data);
      if (!clean || !allowed()) return;
      sendClarity(name, clean);
      try {
        if (allowed() && typeof window.Shopify?.analytics?.publish === 'function') {
          ignoreRejection(window.Shopify.analytics.publish('calm:' + name, { ...context, ...clean }));
        }
      } catch {}
    } catch {}
  }

  function once(owner, key, name, data = {}) {
    try {
      if (!owner || !onceKeys.includes(key) || !payloadFor(name, data)) return;
      let keys = seen.get(owner);
      if (!keys) { keys = new Set(); seen.set(owner, keys); }
      if (keys.has(key)) return;
      keys.add(key);
      emit(name, data);
    } catch {}
  }

  function sizeForVariant(id) {
    try {
      return Object.hasOwn(variantSizes, id) ? variantSizes[id] : undefined;
    } catch {
      return undefined;
    }
  }

  function markGuideIntent(info, event, expected, target) {
    try {
      if (!info || !target || event?.isTrusted !== true || !sizeForVariant(expected)) return;
      const previous = info.querySelector('product-form input[name="id"]')?.value;
      if (String(previous) === String(expected)) return;
      const pending = { expected: String(expected), target };
      guideIntents.set(info, pending);
      queueMicrotask(() => { if (guideIntents.get(info) === pending) guideIntents.delete(info); });
    } catch {}
  }

  function variantIntent(info, event) {
    try {
      const pending = guideIntents.get(info);
      guideIntents.delete(info);
      const guide = pending && event?.target === pending.target;
      if (!guide && event?.isTrusted !== true) return;
      const previous = info.querySelector('product-form input[name="id"]')?.value;
      if (!sizeForVariant(previous)) return;
      return { source: guide ? 'guide' : 'product', previous, expected: guide ? pending.expected : undefined };
    } catch {}
  }

  function variantConfirmed(intent, variant) {
    try {
      if (!intent || confirmedIntents.has(intent)) return;
      confirmedIntents.add(intent);
      const size = sizeForVariant(variant?.id);
      if (intent.expected && String(variant?.id) !== intent.expected) return;
      if (!size || size === sizeForVariant(intent.previous)) return;
      emit('size_selected', { source: intent.source, size });
    } catch {}
  }

  function onClick(event) {
    try {
      if (event.isTrusted !== true || event.defaultPrevented) return;
      const checkout = event.target.closest('button[name="checkout"]');
      if (checkout && !checkout.disabled && checkout.getAttribute('aria-disabled') !== 'true') {
        if (checkout.id === 'CartDrawer-Checkout') emit('checkout_clicked', { source: 'drawer' });
        else if (checkout.id === 'checkout') emit('checkout_clicked', { source: 'cart' });
        return;
      }
      const link = event.target.closest('a');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.getAttribute('href') === '#CalmProductSizes' && document.getElementById('CalmProductSizes')) {
        emit('size_guide_used', { kind: 'jump' });
        return;
      }
      if (link.closest('calm-room-quiz')) return;
      const pair = link.closest('.calm-pair__links');
      const card = link.closest('.card__heading') || (link.matches('.ca-card-action') && link.closest('.card-wrapper'));
      if (!pair && !card) return;
      const path = (link.getAttribute('href') || '').split('?')[0].split('#')[0];
      const match = path.match(/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?products\/([a-z0-9-]+)\/?$/);
      if (!match || !handles.includes(match[1])) return;
      const source = pair ? 'pair' : link.closest('product-recommendations') ? 'recommendation' : 'collection';
      emit('product_selected', { source, handle: match[1] });
    } catch {}
  }

  window.CalmCRO = Object.freeze({ emit, once, sizeForVariant, markGuideIntent, variantIntent, variantConfirmed });
  document.addEventListener('click', onClick);
  document.addEventListener('visitorConsentCollected', syncContext);
  document.addEventListener('load', (event) => {
    if (event.target?.id === 'calm-clarity-tag') syncContext();
  }, true);
  window.addEventListener('pageshow', syncContext);
  syncContext();
  try {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) syncContext();
    });
  } catch {}
})();
